import { vec3 } from 'gl-matrix';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { PlaneView } from '../../../view/views/PlaneView';
import { Line } from './LineDrawAction';

export enum LinePreviewStyle {
    NORMAL,
    CLOSING,
}

/**
 * Handles 2D canvas rendering for the line draw tool preview.
 * Draws the polyline preview, hover point, and snap-to-close
 * visual feedback on the PlaneView's 2D overlay canvas.
 */
export class LinePreviewRenderer {
    private readonly canvasContext: CanvasRenderingContext2D | null;
    private readonly planeView: PlaneView;
    private readonly roi: RegionOfInterest;

    public constructor(planeView: PlaneView, roi: RegionOfInterest) {
        this.planeView = planeView;
        this.roi = roi;

        const canvas = this.planeView.get2DCanvas;
        if (canvas) {
            this.canvasContext = canvas.getContext('2d');
            if (!this.canvasContext) {
                throw new Error('Canvas context unavailable');
            }
        } else {
            throw new Error('Canvas unavailable');
        }
    }

    /**
     * Syncs the 2D canvas drawing-buffer dimensions to the current
     * CSS layout size. Must be called before drawing to avoid
     * coordinate mismatches after viewport resize (e.g. fullscreen).
     * Assigning width/height also clears the canvas automatically.
     */
    private syncCanvasSize(): void {
        const canvas = this.planeView.get2DCanvas;
        if (canvas && this.canvasContext) {
            const w = canvas.offsetWidth;
            const h = canvas.offsetHeight;
            if (
                this.canvasContext.canvas.width !== w ||
                this.canvasContext.canvas.height !== h
            ) {
                this.canvasContext.canvas.width = w;
                this.canvasContext.canvas.height = h;
            }
        }
    }

    private getRoiColor(): { R: number; G: number; B: number } {
        const color = this.roi.color;
        const R = Math.round(color[0] * 255);
        const G = Math.round(color[1] * 255);
        const B = Math.round(color[2] * 255);
        return { R, G, B };
    }

    /**
     * Renders the line preview on the 2D canvas overlay.
     * @param line The current line being drawn, or null.
     * @param style Whether to render in normal or closing style.
     */
    public render(line: Line | null, style: LinePreviewStyle): void {
        const canvas = this.planeView.get2DCanvas;
        if (!canvas || !this.canvasContext) {
            console.error('Canvas or context is undefined.');
            return;
        }

        this.syncCanvasSize();

        const ctx = this.canvasContext;
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (line && line.pointsMm.length > 0) {
            const color = this.getRoiColor();
            ctx.strokeStyle = `rgba(${color.R}, ${color.G}, ${color.B}, 1)`;
            ctx.lineWidth = style === LinePreviewStyle.CLOSING ? 2 : 1;

            ctx.beginPath();
            const firstPt = line.pointsMm[0];
            const firstCanvasCoords =
                this.planeView.canvasCoordsFromPatientCoords(
                    vec3.fromValues(firstPt[0], firstPt[1], firstPt[2])
                );
            ctx.arc(
                firstCanvasCoords[0],
                firstCanvasCoords[1],
                3,
                0,
                2 * Math.PI
            );
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(firstCanvasCoords[0], firstCanvasCoords[1]);

            for (let iPt = 1; iPt < line.pointsMm.length; ++iPt) {
                const point = line.pointsMm[iPt];
                const canvasCoords =
                    this.planeView.canvasCoordsFromPatientCoords(
                        vec3.fromValues(point[0], point[1], point[2])
                    );
                ctx.lineTo(canvasCoords[0], canvasCoords[1]);
            }

            if (line.hoverPointMm) {
                const hoverPt = line.hoverPointMm;
                const hoverCanvasCoords =
                    this.planeView.canvasCoordsFromPatientCoords(
                        vec3.fromValues(hoverPt[0], hoverPt[1], hoverPt[2])
                    );
                ctx.lineTo(hoverCanvasCoords[0], hoverCanvasCoords[1]);
            }

            ctx.stroke();
        }
    }

    /** Clears the 2D canvas overlay. */
    public clear(): void {
        if (this.canvasContext) {
            const { canvas } = this.canvasContext;
            this.canvasContext.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    /** Clears the canvas (alias for cleanup). */
    public dispose(): void {
        this.clear();
    }
}
